import type { Object3D, Scene, PerspectiveCamera, ShaderMaterial } from 'three'
import { Vector3, Mesh } from 'three'
import { disposeMaterial } from '@/lib/three-d-viewer/utils'
import type { ThreeDRenderModule } from '@/lib/three-d-resources/three-d-render-module'

export class TargetVisualizer implements ThreeDRenderModule {
  private uniforms = {
    uPointerPosition: { value: new Vector3() },
    uCameraDistance: { value: 0 },
    uShowing: { value: false },
    uTime: { value: 0 },
  }

  addNode(node: Object3D) {
    node.traverse(this._addShader.bind(this))
  }

  show(): void {
    this.uniforms.uShowing.value = true
  }

  hide(): void {
    this.uniforms.uShowing.value = false
  }

  update(point: Vector3) {
    this.uniforms.uPointerPosition.value.copy(point)
  }

  // render module
  render(_scene: Scene, camera: PerspectiveCamera): void {
    if (this.uniforms.uShowing.value) {
      this.uniforms.uTime.value = performance.now()
      this.uniforms.uCameraDistance.value = camera.position.distanceTo(this.uniforms.uPointerPosition.value)
    }
  }

  resetData(): void {
    this.uniforms.uPointerPosition.value.set(0, 0, 0)
    this.uniforms.uCameraDistance.value = 0
    this.uniforms.uShowing.value = false
  }

  private _addShader(node: Object3D) {
    if (node instanceof Mesh) {
      const shaderMaterial = node.material.clone()
      disposeMaterial(node.material)
      shaderMaterial.onBeforeCompile = (shader: ShaderMaterial) => {
        shader.uniforms.uPointerPosition = this.uniforms.uPointerPosition
        shader.uniforms.uCameraDistance = this.uniforms.uCameraDistance
        shader.uniforms.uShowing = this.uniforms.uShowing
        shader.uniforms.uTime = this.uniforms.uTime
        // The multiple replace statements are to define certain parameters near the beginning
        // of the shader pipeline, but set them near the end (after lighting, texture mapping, etc.)
        // We also need to accommodate different base material types (standard and basic).
        shader.vertexShader = shader.vertexShader.replace(
          `#include <common>`,
          `#include <common>
            #define USE_TRANSMISSION // sets vWorldPosition
            #define USE_UV // sets vUv
            varying vec3 vWorldPosition;`
        )
        shader.vertexShader = shader.vertexShader.replace(
          `#include <worldpos_vertex>`,
          `#include <worldpos_vertex>
            vWorldPosition = worldPosition.xyz;`
        )
        shader.fragmentShader = shader.fragmentShader.replace(
          `#include <common>`,
          `#include <common>
            uniform vec3 uPointerPosition;
            uniform float uCameraDistance;
            uniform bool uShowing;
            uniform float uTime;
            varying vec3 vWorldPosition;`
        )
        shader.fragmentShader = shader.fragmentShader.replace(
          `#include <dithering_fragment>`,
          `#include <dithering_fragment>

            float dist = distance(vWorldPosition, uPointerPosition);
            float radius = uCameraDistance/40.;
            float ringWidth = radius/4.;
            float time = uTime/1500.;
            float innerRadius = 0.25 * radius;
            vec4 cursorColor = mix(vec4(110./255., 231./255., 183./255., 1.), gl_FragColor, (dist-innerRadius)/(radius-innerRadius));

            if (uShowing && dist < radius) {
              vec4 color = gl_FragColor;
              color = mix(cursorColor, color, step(innerRadius, dist));
              color = mix(color, cursorColor, step(innerRadius + fract(time)*(radius-innerRadius), dist));
              color = mix(color, gl_FragColor, step(innerRadius + fract(time)*(radius-innerRadius+ringWidth), dist));
              gl_FragColor = color;
            }`
        )
      }
      node.material = shaderMaterial
      node.material.needsUpdate = true
    }
  }
}
